import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { ArrowRight, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const { subdomainCompany } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const coName = subdomainCompany?.name || "Acteio";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto animate-fade-up">
          <Logo className="mb-12" brand={subdomainCompany} />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Mot de passe oublié</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Réinitialisation</h1>
          <p className="text-muted-foreground mb-8">Saisissez votre email et nous vous enverrons un lien pour créer un nouveau mot de passe {coName}.</p>

          {sent ? (
            <p className="text-sm text-foreground bg-muted border border-border rounded-sm p-4" data-testid="forgot-success">
              Si un compte existe pour cette adresse, un email de réinitialisation vient d'être envoyé. Pensez à vérifier vos spams.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} data-testid="forgot-email"
                  onChange={(e) => setEmail(e.target.value)} placeholder="vous@acteio.com" className="mt-1.5 rounded-sm h-11" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90 text-base font-semibold group">
                {loading ? "Envoi…" : "Envoyer le lien"}
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </form>
          )}

          <p className="mt-6 text-sm text-muted-foreground">
            <Link to="/login" className="text-primary font-medium hover:underline inline-flex items-center" data-testid="goto-login">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Retour à la connexion
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-foreground">
        <img src="https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Acteio" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-3 max-w-md leading-tight">Des propositions commerciales qui closent plus vite.</h2>
          <p className="text-white/70 max-w-md">Créez, envoyez et suivez vos propositions premium. Boostez votre taux de signature.</p>
        </div>
      </div>
    </div>
  );
}
